import React, { useState, useEffect } from 'react';
import {
  Form, Select, Input, Button, message, Card, Alert, Modal,
  Descriptions, Tag, Spin, Table, Switch, Typography, Space,
  Tabs, InputNumber, Row, Col
} from 'antd';
import {
  SearchOutlined, SendOutlined, ShopOutlined, LinkOutlined,
  CheckCircleOutlined, WarningOutlined, ThunderboltOutlined, FormOutlined
} from '@ant-design/icons';
import { http } from '../../utils';
import './index.scss';

const { Option } = Select;
const { Text } = Typography;

const COUNTRY_OPTIONS = ['DE', 'FR', 'BE', 'NL', 'GB'];

const ManualOrderTab = () => {
  const [form] = Form.useForm();
  const [stores, setStores] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const [orderData, setOrderData] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [referralCode, setReferralCode] = useState('');
  const [lakaRecord, setLakaRecord] = useState(null);

  const selectedStore = Form.useWatch('store', form);
  const currentStore = stores.find((s) => s.key === selectedStore);

  const loadStores = () => {
    http.get('/api/manual-webhook/stores').then((res) => {
      if (parseInt(res.code, 10) === 0) {
        setStores(res.data);
      }
    }).catch(() => {
      message.error('Failed to load store configs');
    });
  };

  useEffect(() => {
    loadStores();

    const params = new URLSearchParams(window.location.search);
    const authResult = params.get('auth');
    if (authResult === 'success') {
      message.success('Shopify store connected successfully');
      window.history.replaceState({}, '', window.location.pathname);
    } else if (authResult === 'failed') {
      message.error('Shopify authorization failed, please try again');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const handleConnect = (storeKey) => {
    window.location.href = `/api/manual-webhook/shopify-auth?store=${storeKey}`;
  };

  const handleFetch = async () => {
    const values = await form.validateFields(['store', 'orderNumber']);
    setFetchLoading(true);
    setOrderData(null);
    setLineItems([]);
    setLakaRecord(null);
    setResult(null);
    setError(null);

    try {
      const res = await http.post('/api/manual-webhook/fetch-order', {
        store: values.store,
        orderNumber: values.orderNumber.trim()
      });

      if (parseInt(res.code, 10) === 0) {
        setOrderData(res.data);
        setLineItems(res.data.line_items.map((item) => ({
          ...item,
          insuranceEnabled: item.hasInsurance
        })));
        if (res.data.lakaRecord) {
          setLakaRecord(res.data.lakaRecord);
          message.warning('This order has already been processed by Laka');
        } else {
          message.success(`Order ${res.data.name} loaded`);
        }
      } else {
        setError(res.message || 'Failed to fetch order');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data || e.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setFetchLoading(false);
    }
  };

  const toggleInsurance = (lineItemId, checked) => {
    setLineItems((prev) =>
      prev.map((item) =>
        item.id === lineItemId ? { ...item, insuranceEnabled: checked } : item
      )
    );
  };

  const doSubmit = async () => {
    setSubmitLoading(true);
    setResult(null);
    setError(null);

    const modifiedLineItems = lineItems.map((item) => {
      let properties = [...(item.properties || [])];
      const hasIt = properties.some(
        (p) => p.name === '_insurance_option' && p.value === 'laka'
      );

      if (item.insuranceEnabled && !hasIt) {
        properties.push({ name: '_insurance_option', value: 'laka' });
      } else if (!item.insuranceEnabled && hasIt) {
        properties = properties.filter(
          (p) => !(p.name === '_insurance_option' && p.value === 'laka')
        );
      }

      return { ...item, properties };
    });

    const orderPayload = { ...orderData, line_items: modifiedLineItems };

    try {
      const res = await http.post('/api/manual-webhook/trigger', {
        store: form.getFieldValue('store'),
        order: orderPayload,
        referralCode: referralCode || ''
      });

      if (parseInt(res.code, 10) === 0) {
        message.success('Order processed successfully');
        setResult(res.data);
      } else {
        setError(res.message || 'Failed to process order');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data || e.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!orderData) return;

    const enabledCount = lineItems.filter((i) => i.insuranceEnabled).length;
    if (enabledCount === 0) {
      message.warning('Please enable Laka insurance for at least one product');
      return;
    }

    Modal.confirm({
      title: 'Confirm Send to Laka',
      icon: <WarningOutlined />,
      content: `Are you sure you want to send this order (${orderData.name}) to Laka? ${enabledCount} item(s) will be insured.`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: doSubmit
    });
  };

  const columns = [
    {
      title: 'Product',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true
    },
    {
      title: 'Variant',
      dataIndex: 'variant_title',
      key: 'variant_title',
      width: 180,
      render: (text) => text || '-'
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: 140,
      render: (text) => text || '-'
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      render: (price) => `${price} ${orderData?.currency || ''}`
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
      align: 'center'
    },
    {
      title: 'Laka Insurance',
      key: 'insurance',
      width: 140,
      align: 'center',
      render: (_, record) => (
        <Switch
          checked={record.insuranceEnabled}
          onChange={(checked) => toggleInsurance(record.id, checked)}
          checkedChildren="ON"
          unCheckedChildren="OFF"
        />
      )
    }
  ];

  return (
    <div>
      <Card title="Shopify Store Connection" className="manual-webhook-card" size="small">
        <div className="store-connection-list">
          {stores.map((s) => (
            <div key={s.key} className="store-connection-item">
              <div className="store-connection-info">
                <Text strong>{s.label}</Text>
                <Text type="secondary"> ({s.countries})</Text>
                <Tag style={{ marginLeft: 8 }}>{s.domain}</Tag>
                {s.connected ? (
                  <Tag color="green" icon={<CheckCircleOutlined />}>Connected</Tag>
                ) : (
                  <Tag color="red">Not Connected</Tag>
                )}
              </div>
              <div>
                {s.connected ? (
                  <Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Authorized: {new Date(s.authorizedAt).toLocaleString()}
                    </Text>
                    <Button size="small" onClick={() => handleConnect(s.key)}>
                      Re-connect
                    </Button>
                  </Space>
                ) : (
                  <Button
                    type="primary"
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={() => handleConnect(s.key)}
                  >
                    Connect Shopify
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card title="Step 1 — Fetch Order" className="manual-webhook-card">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ store: 'EU' }}
        >
          <Form.Item
            label="Store"
            name="store"
            rules={[{ required: true, message: 'Please select a store' }]}
          >
            <Select placeholder="Select a store">
              {stores.map((s) => (
                <Option key={s.key} value={s.key} disabled={!s.connected}>
                  {s.label} ({s.countries}) — {s.domain}
                  {!s.connected ? ' (Not Connected)' : ''}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {currentStore && (
            <div className="store-info">
              <Tag color={currentStore.connected ? 'green' : 'red'}>
                {currentStore.domain}
              </Tag>
              <a href={currentStore.adminUrl} target="_blank" rel="noopener noreferrer">
                Open Shopify Admin
              </a>
            </div>
          )}

          <Form.Item
            label="Order Number"
            name="orderNumber"
            rules={[{ required: true, message: 'Please input the order number' }]}
          >
            <Input
              placeholder="e.g. 1001 or #1001"
              prefix={<ShopOutlined />}
              allowClear
              onPressEnter={handleFetch}
            />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              loading={fetchLoading}
              icon={<SearchOutlined />}
              onClick={handleFetch}
              disabled={currentStore && !currentStore.connected}
            >
              Fetch Order
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {fetchLoading && (
        <div className="manual-webhook-loading">
          <Spin tip="Fetching order from Shopify..." />
        </div>
      )}

      {orderData && (
        <Card title="Step 2 — Review Order & Enable Insurance" className="manual-webhook-card">
          {lakaRecord && (
            <Alert
              message="This order has already been processed by Laka"
              description={
                <span>
                  State: <Tag color="blue">{
                    { '0': 'Pending', '1': 'Quoted', '2': 'Handed Off' }[lakaRecord.state] || lakaRecord.state
                  }</Tag>
                  {lakaRecord.referralCode && (
                    <>Referral Code: <Tag>{lakaRecord.referralCode}</Tag></>
                  )}
                  Processed at: {new Date(lakaRecord.createdAt).toLocaleString()}
                </span>
              }
              type="warning"
              style={{ marginBottom: 16 }}
            />
          )}

          <Descriptions column={2} bordered size="small" className="order-overview">
            <Descriptions.Item label="Order">{orderData.name}</Descriptions.Item>
            <Descriptions.Item label="Order ID">{orderData.id}</Descriptions.Item>
            <Descriptions.Item label="Customer">
              {orderData.customer?.first_name} {orderData.customer?.last_name}
            </Descriptions.Item>
            <Descriptions.Item label="Email">{orderData.contact_email}</Descriptions.Item>
            <Descriptions.Item label="Total">{orderData.total_price} {orderData.currency}</Descriptions.Item>
            <Descriptions.Item label="Financial Status">
              <Tag color={orderData.financial_status === 'paid' ? 'green' : 'orange'}>
                {orderData.financial_status}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Shipping" span={2}>
              {orderData.shipping_address?.city}, {orderData.shipping_address?.country} ({orderData.shipping_address?.country_code})
            </Descriptions.Item>
          </Descriptions>

          <div className="line-items-section">
            <Text strong>Line Items — toggle Laka insurance for each product:</Text>
            <Table
              columns={columns}
              dataSource={lineItems}
              rowKey="id"
              pagination={false}
              size="small"
              style={{ marginTop: 12 }}
              rowClassName={(record) => record.insuranceEnabled ? 'row-insurance-on' : ''}
            />
          </div>

          <div className="submit-section">
            <div className="referral-code-row">
              <Text strong>Referral Code</Text>
              <Text type="secondary" style={{ marginLeft: 8 }}>
                (optional — leave empty to use the current active code)
              </Text>
            </div>
            <Input
              placeholder="e.g. TENWAYS"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              style={{ width: 300, marginBottom: 16 }}
              allowClear
              disabled={!!lakaRecord}
            />
            <div>
              <Space>
                <Button
                  type="primary"
                  size="large"
                  loading={submitLoading}
                  disabled={submitLoading || !!lakaRecord}
                  icon={<SendOutlined />}
                  onClick={handleSubmit}
                >
                  Send to Laka
                </Button>
                {lakaRecord ? (
                  <Text type="danger">Order already processed — cannot resubmit</Text>
                ) : (
                  <Text type="secondary">
                    {lineItems.filter((i) => i.insuranceEnabled).length} of {lineItems.length} items will be insured
                  </Text>
                )}
              </Space>
            </div>
          </div>
        </Card>
      )}

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginTop: 16 }}
          onClose={() => setError(null)}
        />
      )}

      {result && (
        <Card title="Result" className="manual-webhook-result" style={{ marginTop: 16 }}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Order ID">{result.orderId}</Descriptions.Item>
            <Descriptions.Item label="Order Name">{result.name}</Descriptions.Item>
            <Descriptions.Item label="Customer Email">{result.customerEmail}</Descriptions.Item>
            <Descriptions.Item label="Total Price">{result.totalPrice} {result.currency}</Descriptions.Item>
            <Descriptions.Item label="In Region">
              <Tag color={result.inRegion ? 'green' : 'red'}>
                {result.inRegion ? `Yes (${result.region?.toUpperCase()})` : 'No'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Insurance Items">{result.insuranceCount}</Descriptions.Item>
            <Descriptions.Item label="Laka Triggered" span={2}>
              <Tag color={result.lakaTriggered ? 'green' : 'orange'}>
                {result.lakaTriggered ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
          {!result.lakaTriggered && result.insuranceCount === 0 && (
            <Alert
              message="No insurance items were processed"
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
          {result.lakaTriggered && (
            <Alert
              message="Laka processing has been triggered. Check the Order and Quote pages to monitor progress."
              type="success"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
        </Card>
      )}
    </div>
  );
};

const CustomLakaTab = () => {
  const [form] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const submitCustomRequest = async () => {
    const values = await form.validateFields();
    setSubmitLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await http.post('/api/manual-webhook/custom-trigger', {
        countryCode: values.countryCode,
        firstName: values.firstName.trim(),
        lastName: values.lastName.trim(),
        email: values.email.trim(),
        phoneNumber: values.phoneNumber.trim(),
        postalCode: values.postalCode.trim(),
        productName: values.productName.trim(),
        variantTitle: values.variantTitle.trim(),
        price: values.price,
        referralCode: values.referralCode ? values.referralCode.trim() : ''
      }, { timeout: 30000 });

      if (parseInt(res.code, 10) === 0) {
        message.success('Custom Laka request sent successfully');
        setResult(res.data);
      } else {
        setError(res.message || 'Failed to send custom request');
      }
    } catch (e) {
      const msg = e.response?.data?.message || e.response?.data || e.message || 'Request failed';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (submitLoading) return;
    const values = await form.validateFields();
    Modal.confirm({
      title: 'Confirm Send to Laka',
      icon: <WarningOutlined />,
      content: `Send custom Laka request for ${values.countryCode} / ${values.productName}?`,
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: submitCustomRequest
    });
  };

  return (
    <div>
      <Card title="Custom Laka Request" className="manual-webhook-card">
        <Form
          form={form}
          layout="vertical"
          initialValues={{ countryCode: 'DE' }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Country Code"
                name="countryCode"
                rules={[{ required: true, message: 'Please select country code' }]}
              >
                <Select placeholder="Select country">
                  {COUNTRY_OPTIONS.map((code) => (
                    <Option key={code} value={code}>{code}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="First Name"
                name="firstName"
                rules={[{ required: true, message: 'Please input first name' }]}
              >
                <Input placeholder="e.g. Alice" maxLength={64} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Last Name"
                name="lastName"
                rules={[{ required: true, message: 'Please input last name' }]}
              >
                <Input placeholder="e.g. Smith" maxLength={64} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Email"
                name="email"
                rules={[
                  { required: true, message: 'Please input email' },
                  { type: 'email', message: 'Please input valid email address' }
                ]}
              >
                <Input placeholder="e.g. alice@example.com" maxLength={128} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Phone Number"
                name="phoneNumber"
                rules={[{ required: true, message: 'Please input phone number' }]}
              >
                <Input placeholder="e.g. +31 612345678" maxLength={32} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Postal Code"
                name="postalCode"
                rules={[{ required: true, message: 'Please input postal code' }]}
              >
                <Input placeholder="e.g. 1012AB" maxLength={20} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Product Name"
                name="productName"
                rules={[{ required: true, message: 'Please input product name' }]}
              >
                <Input placeholder="e.g. TENWAYS CGO600 Pro | Plus - CGO600 Pro New Edition (Belt Drive) / XL / Pebble Grey" maxLength={128} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Variant Title"
                name="variantTitle"
                rules={[{ required: true, message: 'Please input variant title' }]}
              >
                <Input placeholder="e.g. CGO600 Pro New Edition (Belt Drive) / XL / Pebble Grey" maxLength={128} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Price"
                name="price"
                rules={[{ required: true, message: 'Please input product price' }]}
              >
                <InputNumber
                  min={0.01}
                  placeholder="e.g. 2199"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Referral Code (optional)"
                name="referralCode"
              >
                <Input placeholder="e.g. TENWAYS" maxLength={64} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Button
              type="primary"
              size="large"
              icon={<SendOutlined />}
              loading={submitLoading}
              disabled={submitLoading}
              onClick={handleSubmit}
            >
              Send to Laka (Quote + Handoff)
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          closable
          style={{ marginTop: 16 }}
          onClose={() => setError(null)}
        />
      )}

      {result && (
        <Card title="Custom Request Result" className="manual-webhook-result" style={{ marginTop: 16 }}>
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Quote ID">{result.quoteId || '-'}</Descriptions.Item>
            <Descriptions.Item label="Country">{result.countryCode}</Descriptions.Item>
            <Descriptions.Item label="Handoff Status">
              <Tag color={result.handoffSuccess ? 'green' : 'red'}>
                {result.handoffSuccess ? 'Success' : 'Failed'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Referral Code">{result.usedReferralCode || '-'}</Descriptions.Item>
            <Descriptions.Item label="Product" span={2}>{result.productName}</Descriptions.Item>
          </Descriptions>
          {result.handoffSuccess ? (
            <Alert
              message="Quote and handoff completed successfully."
              type="success"
              showIcon
              style={{ marginTop: 12 }}
            />
          ) : (
            <Alert
              message="Handoff failed"
              description={result.error || 'Unknown error'}
              type="error"
              showIcon
              style={{ marginTop: 12 }}
            />
          )}
        </Card>
      )}
    </div>
  );
};

const ManualWebhook = () => (
  <div>
    <div className="manual-webhook-header">Manual Webhook Trigger</div>
    <div className="manual-webhook-content">
      <Tabs
        defaultActiveKey="order-trigger"
        items={[
          {
            key: 'order-trigger',
            label: <span><ThunderboltOutlined /> Order Trigger</span>,
            children: <ManualOrderTab />
          },
          {
            key: 'custom-laka',
            label: <span><FormOutlined /> Custom Laka Request</span>,
            children: <CustomLakaTab />
          }
        ]}
      />
    </div>
  </div>
);

export default ManualWebhook;
